import { useCallback, useState } from "react";
import { useRouter } from "next/router";
import { searchHoliday as searchHolidayApi } from "@/services/api/holidays";
import {
  buildSearchListHref,
  createSearchDraft,
  findSearchMatch,
  getSearchPrefix,
  isSameSearchDestination,
  resolveEffectiveSearchMonth,
  SEARCH_MIN_CHARS,
} from "@/utils/holidaySearch";
import { withRegionPath } from "@/utils/session";
import { beginListingSearch } from "@/utils/listingFilterSession";

const EMPTY_DRAFT = {
  label: "",
  month: "",
  months: [],
  selection: null,
};

export default function useHolidayEditSearch({ urlParams, pageTitle = "" }) {
  const router = useRouter();
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [locationOpen, setLocationOpen] = useState(false);

  const initDraft = useCallback(async () => {
    const prefix = getSearchPrefix(urlParams, pageTitle);
    const preferredMonth = urlParams.month || "";

    if (!prefix) {
      setDraft(EMPTY_DRAFT);
      return;
    }

    try {
      const response = await searchHolidayApi(
        prefix.trim().length >= SEARCH_MIN_CHARS ? prefix.trim() : undefined
      );
      const match = findSearchMatch(response, { prefix, urlParams });
      setDraft(match ? createSearchDraft(match, preferredMonth) : { ...EMPTY_DRAFT, label: prefix });
    } catch {
      setDraft({ ...EMPTY_DRAFT, label: prefix });
    }
  }, [urlParams, pageTitle]);

  const openLocationSearch = useCallback(() => {
    setLocationOpen(true);
  }, []);

  const closeLocationSearch = useCallback(() => {
    setLocationOpen(false);
  }, []);

  const selectLocation = useCallback((item) => {
    setDraft(createSearchDraft(item));
    setLocationOpen(false);
  }, []);

  const setMonth = useCallback((month) => {
    setDraft((prev) => ({ ...prev, month }));
  }, []);

  const setDestinationQuery = useCallback((label) => {
    setDraft((prev) => {
      if (prev.selection?.label === label) {
        return prev;
      }

      return {
        ...prev,
        label,
        selection: null,
        month: "",
        months: [],
      };
    });
  }, []);

  const applySearch = useCallback(() => {
    const destinationChanged = !isSameSearchDestination(draft.selection, urlParams);
    const month = destinationChanged
      ? String(draft.month || "").trim()
      : resolveEffectiveSearchMonth(draft, urlParams);

    if (!destinationChanged && draft.months.length > 0 && !month) {
      return { success: false, error: "month" };
    }

    const href = draft.selection
      ? buildSearchListHref(draft.selection, month)
      : buildSearchListHref({ label: draft.label }, month);

    beginListingSearch("holiday");
    router.push(withRegionPath(href));
    return { success: true };
  }, [draft, router, urlParams]);

  return {
    draft,
    locationOpen,
    initDraft,
    openLocationSearch,
    closeLocationSearch,
    selectLocation,
    setMonth,
    setDestinationQuery,
    applySearch,
  };
}
